import { FEATURE_FLAG } from "@/common/enums/featureFlag";

/**
 * Where every record and index page of a workspace lives (GEN5-5322): one
 * registry, so the app's links, Genie's links and `page.open` build the same
 * route. Each segment and feature flag is the route's own in
 * `common/routing/paths.ts`; a test holds the two together (the SDK sits below
 * routing, so it cannot read paths.ts itself).
 */

/** A route's feature flag: off, the app shows Not Found there. */
type RouteFeatureFlag = (typeof FEATURE_FLAG)[keyof typeof FEATURE_FLAG];

type RecordRouteDefinition = {
  /** The record's segment under the workspace: `…/workspaces/:id/<segment>/:recordId`. */
  segment: string;
  /** The record's page title, as its route names it. */
  title: string;
  /** What one record is called in the UI. */
  label: string;
  /** A Material Symbol: the record's icon in the app's navigation. */
  icon: string;
  featureFlag: RouteFeatureFlag | null;
};

/**
 * The records that have a page of their own, by the kind Genie names them
 * with (camelCase, as `genie://<kind>/<id>` carries it).
 */
export const RECORD_ROUTES = {
  stakeholder: {
    segment: "stakeholders",
    title: "Profile",
    label: "Stakeholder",
    icon: "person",
    featureFlag: null,
  },
  touchpoint: {
    segment: "touchpoints",
    title: "Touchpoint Details",
    label: "Touchpoint",
    icon: "calendar_today",
    featureFlag: FEATURE_FLAG.TOUCHPOINT,
  },
  plan: {
    segment: "plans",
    title: "Project Details",
    label: "Project",
    icon: "landscape",
    featureFlag: FEATURE_FLAG.PLAN,
  },
  task: {
    segment: "tasks",
    title: "Task Details",
    label: "Task",
    icon: "fact_check",
    featureFlag: FEATURE_FLAG.TASK,
  },
  // My Feed's content page.
  media: {
    segment: "my-feed",
    title: "Content Summary",
    label: "Media",
    icon: "full_coverage",
    featureFlag: null,
  },
  stakeholderGroup: {
    segment: "stakeholder-groups",
    title: "Group",
    label: "Stakeholder group",
    icon: "group",
    featureFlag: null,
  },
  network: {
    segment: "networks",
    title: "Network Graph",
    label: "Network",
    icon: "hub",
    featureFlag: FEATURE_FLAG.NETWORKS,
  },
  newsletter: {
    segment: "newsletters",
    title: "Newsletter Details",
    label: "Newsletter",
    icon: "newsmode",
    featureFlag: FEATURE_FLAG.NEWSLETTER,
  },
  dashboard: {
    segment: "dashboards",
    title: "Dashboard detail",
    label: "Dashboard",
    icon: "dashboard",
    featureFlag: FEATURE_FLAG.DASHBOARDS,
  },
  userGroup: {
    segment: "user-groups",
    title: "User Group Details",
    label: "User group",
    icon: "supervised_user_circle",
    featureFlag: null,
  },
} as const satisfies Record<string, RecordRouteDefinition>;

export type RecordKind = keyof typeof RECORD_ROUTES;

export const RECORD_KINDS = [
  "stakeholder",
  "touchpoint",
  "plan",
  "task",
  "media",
  "stakeholderGroup",
  "network",
  "newsletter",
  "dashboard",
  "userGroup",
] as const satisfies readonly RecordKind[];

export const isRecordKind = (value: unknown): value is RecordKind =>
  typeof value === "string" && RECORD_KINDS.some((kind) => kind === value);

type PageRouteDefinition = {
  /** The page's path under the workspace, without a leading slash. */
  path: string;
  title: string;
  featureFlag: RouteFeatureFlag | null;
};

/** The workspace's index pages, by the name `page.open` takes. */
export const PAGE_ROUTES = {
  stakeholders: {
    path: "stakeholders",
    title: "Stakeholders",
    featureFlag: null,
  },
  stakeholderGroups: {
    path: "stakeholder-groups",
    title: "Stakeholder Groups",
    featureFlag: null,
  },
  touchpoints: {
    path: "touchpoints",
    title: "Touchpoints",
    featureFlag: FEATURE_FLAG.TOUCHPOINT,
  },
  plans: { path: "plans", title: "Projects", featureFlag: FEATURE_FLAG.PLAN },
  tasks: { path: "tasks", title: "Tasks", featureFlag: FEATURE_FLAG.TASK },
  myFeed: { path: "my-feed", title: "My Feed", featureFlag: null },
  contentSettings: {
    path: "content-settings",
    title: "Content Settings",
    featureFlag: null,
  },
  issues: { path: "issues", title: "Issue Settings", featureFlag: null },
  insights: { path: "insights", title: "Insights", featureFlag: null },
  dashboards: {
    path: "dashboards",
    title: "Dashboards",
    featureFlag: FEATURE_FLAG.DASHBOARDS,
  },
  allDashboards: {
    path: "dashboards/browse",
    title: "All Dashboards",
    featureFlag: FEATURE_FLAG.DASHBOARDS,
  },
  networks: {
    path: "networks",
    title: "Stakeholder Networks",
    featureFlag: FEATURE_FLAG.NETWORKS,
  },
  newsletters: {
    path: "newsletters",
    title: "Create & Send Newsletters",
    featureFlag: FEATURE_FLAG.NEWSLETTER,
  },
  customFields: {
    path: "custom-fields",
    title: "Custom Fields",
    featureFlag: null,
  },
  userGroups: { path: "user-groups", title: "User Groups", featureFlag: null },
} as const satisfies Record<string, PageRouteDefinition>;

export type PageKey = keyof typeof PAGE_ROUTES;

export const PAGE_KEYS = [
  "stakeholders",
  "stakeholderGroups",
  "touchpoints",
  "plans",
  "tasks",
  "myFeed",
  "contentSettings",
  "issues",
  "insights",
  "dashboards",
  "allDashboards",
  "networks",
  "newsletters",
  "customFields",
  "userGroups",
] as const satisfies readonly PageKey[];

export const isPageKey = (value: unknown): value is PageKey =>
  typeof value === "string" && PAGE_KEYS.some((key) => key === value);

/** The workspace a route is under. Ids as numbers or as the route's strings. */
export type RouteScope = {
  organizationId: number | string;
  workspaceId: number | string;
};

/** `/organizations/:organizationId/workspaces/:workspaceId`. */
export const workspacePath = ({
  organizationId,
  workspaceId,
}: RouteScope): string =>
  `/organizations/${organizationId}/workspaces/${workspaceId}`;

/** A record's own page. */
export const recordPath = (
  scope: RouteScope,
  kind: RecordKind,
  id: number | string
): string => `${workspacePath(scope)}/${RECORD_ROUTES[kind].segment}/${id}`;

/** An index page of the workspace. */
export const pagePath = (scope: RouteScope, page: PageKey): string =>
  `${workspacePath(scope)}/${PAGE_ROUTES[page].path}`;

/** The record kind whose pages live under this segment, or null. */
export const recordKindOfSegment = (segment: string): RecordKind | null =>
  RECORD_KINDS.find((kind) => RECORD_ROUTES[kind].segment === segment) ?? null;
